import {
  IonButton,
  IonButtons,
  IonContent,
  IonHeader,
  IonIcon,
  IonItem,
  IonLabel,
  IonModal,
  IonTitle,
  IonToolbar,
} from '@ionic/react';
import { close, closeCircleOutline, openOutline } from 'ionicons/icons';
import { ReactElement, useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  ScratchpadBucket,
  ScratchpadBucketScope,
  ScratchpadBucketType,
  useGetScratchpadBucketQuery,
} from '../../api/graphql/generated';
import { WidgetLabel } from '../../scratchpad/application/shared/component/widget/widget-label';
import { SystemUnit } from '../../scratchpad/application/shared/model/flow.types';
import { useConfirmActionAlert } from '../alert/confirmation-alert';
import { ScratchpadBucketExplorer } from './scratchpad-bucket-explorer';
import { ScratchpadBucketVersionBadge } from './scratchpad-bucket-version-badge';

interface ScratchpadBucketSelectorProps {
  value?: string;
  onChange: (bucket: ScratchpadBucket) => void;
  parentId: string;
  scratchpadAppSlug: string;
  scope: ScratchpadBucketScope;
  type: ScratchpadBucketType;
  system: SystemUnit;
  disabled?: boolean;
  presentingElement?: HTMLElement;
  implicitSearchTags?: Record<string, unknown>;
  getBucketSubheading?: (bucket: ScratchpadBucket) => string | undefined;
  disableEdit?: boolean;
  disableView?: boolean;
  label: string;
  placeholder?: string;
  referenceVersion?: number;
  onVersionUpdateRequested?: (bucket: ScratchpadBucket) => void;
  requireConfirmation?: boolean;
  confirmationMessage?: string;
  selectionLabel?: string;
}

export const ScratchpadBucketSelector = (
  props: ScratchpadBucketSelectorProps
): ReactElement => {
  const {
    value,
    onChange,
    parentId,
    scratchpadAppSlug,
    scope,
    type,
    system,
    disabled,
    presentingElement,
    implicitSearchTags = {},
    getBucketSubheading = () => undefined,
    disableEdit,
    disableView,
    label,
    placeholder,
    referenceVersion,
    onVersionUpdateRequested,
    requireConfirmation,
    confirmationMessage,
    selectionLabel,
  } = props;

  const { i18n } = useTranslation();
  const [isOpen, setIsOpen] = useState(false);
  const confirmActionAlert = useConfirmActionAlert();

  // Get the selected bucket's name if we have a value
  const { data: bucketData } = useGetScratchpadBucketQuery({
    variables: {
      input: {
        id: value || '',
      },
    },
    skip: !value,
    fetchPolicy: 'cache-and-network',
  });

  const handleSelection = useCallback(
    (bucket: ScratchpadBucket) => {
      if (requireConfirmation && bucket.id) {
        confirmActionAlert({
          header: i18n.t('common:confirm'),
          message: confirmationMessage ?? i18n.t('common:are_you_sure'),
          confirmationAction: () => {
            onChange(bucket);
            setIsOpen(false);
          },
        })();
      } else {
        onChange(bucket);
        setIsOpen(false);
      }
    },
    [
      onChange,
      requireConfirmation,
      confirmationMessage,
      confirmActionAlert,
      i18n,
    ]
  );

  const handleVersionUpdate = useCallback(
    (bucket: ScratchpadBucket) => {
      if (requireConfirmation) {
        confirmActionAlert({
          header: i18n.t('common:beacon_app_update_available'),
          message: confirmationMessage ?? i18n.t('common:are_you_sure'),
          confirmationAction: () => onVersionUpdateRequested?.(bucket),
        })();
      } else {
        onVersionUpdateRequested?.(bucket);
      }
    },
    [
      onVersionUpdateRequested,
      requireConfirmation,
      confirmationMessage,
      confirmActionAlert,
      i18n,
    ]
  );

  return (
    <>
      <IonItem
        lines="full"
        className="widget"
        href=""
        disabled={disabled}
        onClick={(e) => {
          e.preventDefault();
          if (!disabled) {
            setIsOpen(true);
          }
        }}
        detail={false}
      >
        <IonLabel position="fixed">
          <WidgetLabel label={label} />
        </IonLabel>
        <IonLabel
          color={bucketData?.getScratchpadBucket.name ? undefined : 'medium'}
          className="ion-text-wrap ion-text-right"
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'flex-end',
            gap: '8px',
          }}
        >
          <span>
            {bucketData?.getScratchpadBucket.name ||
              (placeholder ?? i18n.t('common:select_from_library'))}
          </span>
          {bucketData?.getScratchpadBucket &&
            referenceVersion !== undefined && (
              <ScratchpadBucketVersionBadge
                currentVersion={bucketData.getScratchpadBucket.version}
                referenceVersion={referenceVersion}
                onClick={() =>
                  handleVersionUpdate(bucketData.getScratchpadBucket)
                }
              />
            )}
        </IonLabel>
        {!disabled && (
          <>
            {bucketData?.getScratchpadBucket ? (
              <IonIcon
                slot="end"
                icon={closeCircleOutline}
                color="medium"
                size="small"
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  handleSelection({ id: '', data: null } as ScratchpadBucket);
                }}
              />
            ) : (
              <IonIcon
                slot="end"
                icon={openOutline}
                color="medium"
                size="small"
              />
            )}
          </>
        )}
      </IonItem>

      <IonModal
        isOpen={isOpen}
        onDidDismiss={() => setIsOpen(false)}
        presentingElement={presentingElement}
      >
        <IonHeader>
          <IonToolbar>
            <IonTitle>{i18n.t('common:select_from_library')}</IonTitle>
            <IonButtons slot="end">
              <IonButton onClick={() => setIsOpen(false)}>
                <IonIcon icon={close} />
              </IonButton>
            </IonButtons>
          </IonToolbar>
        </IonHeader>
        <IonContent>
          <ScratchpadBucketExplorer
            parentId={parentId}
            scratchpadAppSlug={scratchpadAppSlug}
            scope={scope}
            type={type}
            system={system}
            implicitSearchTags={implicitSearchTags}
            getBucketSubheading={getBucketSubheading}
            disableEdit={disableEdit}
            disableView={disableView}
            onSelection={handleSelection}
            selectionLabel={selectionLabel}
          />
        </IonContent>
      </IonModal>
    </>
  );
};
